import { utils } from './utils.js';
import { cameras } from './view/cameras.js';
import { lang } from './lang.js';
import { userimage } from './tools/userimage.js';
import { myToolbar } from './tools/toolbar.js';
import { guides } from './view/guides.js';
import { beads } from './beads.js';
import { settings } from './settings.js';
import { modes } from './modes.js';
import { palette } from './palette.js';
import { beadLibrary } from './tools/beadLibrary.js';
import { myInput } from './input.js';
import { save } from './projects/save.js';

export const menu = {};

menu.show = () => { // click gear icon

    // save file if not just saved
    save.currentlyOpenFile();

    modes.start('browser');
    b3.store.mySet('currentScreen', 'menu');

    // trigger resets on menus:
    // - resets export settings
    // - resets browser so project thumbnail is up-to-date
    b3.ee.emit('launchMenu');

    // update table so it reflects palette
    menu.resetPaletteTable();
};

menu.getDisplayRowsAndColumns = () => settings.pattern.getDisplayRowsAndColumns ?
    settings.pattern.getDisplayRowsAndColumns({rows: settings.rows, columns: settings.columns}):
    [settings.rows, settings.columns];

menu.returnToCanvas = () => {
    beadLibrary.closePanel(); // new
    b3.store.mySet('showingModal', null);
    b3.store.mySet('currentScreen', 'my-phaser-container');
    myInput.pointerEnabled = true;
    myInput.disableTyping();
    modes.start('draw');
    cameras.resize();
    beads.refreshCanvas();
};

menu.getBeadLength = library => {
    return [
        {beadShape: 'plastic', ratio: 1},
        {beadShape: 'cylinder', ratio: 46/60},
        {beadShape: 'round', ratio: 47/60},
    ].find(element => element.beadShape === library.shape).ratio * library.diameter;
};

menu.alterProject = async ({
    chosenLibrary, chosenPattern, title, tags, description, instructions, credits,
    rows, columns, renameColors, beadDisplayHeight, diameter, width, ignoreLastRow, reload
}) => { // called on edit project menu - submit button

    const shouldUpdateTagList = settings.tags !== tags;

    settings.setTitle(title)
    settings.description = description;
    settings.instructions = instructions;
    settings.credits = credits;
    settings.tags = tags;

    settings.ignoreLastRow = ignoreLastRow;
    settings.rows = rows;
    settings.columns = columns;
    settings.diameter = diameter;
    settings.width = width;
    settings.beadDisplayHeight = beadDisplayHeight;
    settings.library = chosenLibrary;

    const oldPattern = settings.pattern?.name;
    settings.pattern = chosenPattern;

    b3.store.mySet('pattern', settings.pattern);

    // should overwrite row offsets when reloading as a new pattern
    oldPattern === chosenPattern.name ?
        beads.truncateRowOffset():
        beads.resetRowOffset();

    // only star, square, triangle
    if (settings.pattern.alterProject) {
        settings.pattern.alterProject();
    }

    palette.initializeSwatches({renameColors});
    const msg = lang.get('project updated notice', 'Project updated');
    menu.showInfoBox({msg});
    save.currentlyOpenFile({reload});

    if (shouldUpdateTagList) save.updateTagList();
};

menu.createBeadSoup = list => {
    palette.beadSoup = [];
    list.forEach(obj => {
        palette.beadSoup.push(obj.id);
    });
    myToolbar.enableTool('Soup');
    const msg = lang.get('new bead soup created notice', 'New bead soup created');
    menu.showInfoBox({msg});
};

menu.closePanels = () => {
    b3.store.mySet('showingPanel', null);

    if (b3.store.state.showingModal === 'library-modal') {
        beadLibrary.closePanel();
    }
};

menu.resetPaletteTable = () => {
    const tableData = [];

    b3.store.state.swatches.forEach(element => {
        const mySecondColor = (element.secondColor === undefined) ? null : element.secondColor;
        const myObj = {
            id: element.id,
            rt: element.rt,
            description: element.description,
            letterCode: element.letterCode,
            fillColor: utils.colorToHex(element.fillColor),
            secondColor: (mySecondColor === null) ? '#FFFFFF' : utils.colorToHex(element.secondColor),
            finishName: palette.finishList
                .find(_element => _element.lower === element.finishName)?.display || 'Plastic',
            inner: palette.innerList
                .find(_element => _element.lower === element.inner)?.display || '',
            beadCount: element.getBeadCount()
        };
        tableData.push(myObj);
    });

    b3.ee.emit('renderPaletteTable', tableData);
};

menu.setSimpleMode = checked => {
    settings.renderColorOnly = checked;
    b3.store.state.swatches.forEach(s => s.render());
    beads.refreshCanvas();
};

menu.showGuides = shouldShow => {
    if (!shouldShow) return guides.destroyAll();

    const xSpacing = settings.guideSpacing.x;
    const ySpacing = settings.guideSpacing.y;
    const color = parseInt('0x' + settings.guidesColor.slice(1));

    if (xSpacing < 1 || ySpacing < 1) return;

    guides.set({xSpacing, ySpacing, color});
};

menu.showBake = () => {
    userimage.ignoreBake = false;
    b3.store.mySet('showingModal', 'bake-image');
};

menu.closeBakeDialog = () => {
    userimage.ignoreBake = true; // stops bake being triggered
    b3.store.mySet('showingModal', null);
};

menu.showInfoBox = ({msg, warning=false}) => {
    const icon = warning ? '✖ ' : '✓ ';
    b3.store.mySet('infoBox.message', icon + msg);
    b3.store.mySet('infoBox.visible', true);
    b3.store.mySet('infoBox.warning', warning);
    setTimeout(() => {
        b3.store.mySet('infoBox.visible', false);
        b3.store.mySet('infoBox.warning', false);
    }, 1500);
};

b3.menu = {};

[
    'closeBakeDialog', 'returnToCanvas', 'alterProject',
    'createBeadSoup', 'showGuides'
].forEach(element => {
    if (!menu[element]) console.warn('missing menu element', element);
    b3.menu[element] = menu[element];
});


b3.renderModal = (title, yesFn, noFn, hasInputBox=false) => {

    b3.store.mySet('dialogBox.title', title);
    b3.store.mySet('dialogBox.showTextInput', hasInputBox);
    b3.store.mySet('dialogBox.textInputValue', '');

    b3.modalSuccess = yesFn;
    b3.modalCancel = noFn || b3.hideModal;

    b3.store.mySet('dialogBox.visible', true);
};

b3.hideModal = () => {
    b3.store.mySet('dialogBox.visible', false);
};